import type { HeartRateEvent } from '@model/DeviceConfiguration';
import type { DuplicateIssue, GapIssue, HrDataIssues } from './types';

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export const normalizeTimestampToSecond = (timestamp: number) =>
  Math.floor(timestamp / 1000) * 1000;

const compactEvent = (event: HeartRateEvent) => ({
  timestamp: event.timestamp,
  hr: event.hr,
  hrv: event.hrv,
  lf: event.lf,
  hf: event.hf,
  deviceCode: event.deviceCode,
});

export const detectHrDataIssues = (events: HeartRateEvent[]): HrDataIssues => {
  const validEvents = events.filter(event => isFiniteNumber(event.timestamp));
  const grouped = new Map<number, HeartRateEvent[]>();

  validEvents.forEach(event => {
    const second = normalizeTimestampToSecond(event.timestamp);
    const current = grouped.get(second) ?? [];
    current.push(event);
    grouped.set(second, current);
  });

  const duplicates: DuplicateIssue[] = Array.from(grouped.entries())
    .filter(([, group]) => group.length > 1)
    .sort((left, right) => left[0] - right[0])
    .map(([secondTimestamp, groupEvents]) => ({
      secondTimestamp,
      events: groupEvents.map(compactEvent),
    }));

  const uniqueSeconds = Array.from(grouped.entries())
    .sort((left, right) => left[0] - right[0])
    .map(([secondTimestamp, groupEvents]) => ({
      secondTimestamp,
      representative: groupEvents[0],
    }));

  const gaps: GapIssue[] = [];

  for (let index = 0; index < uniqueSeconds.length - 1; index += 1) {
    const current = uniqueSeconds[index];
    const next = uniqueSeconds[index + 1];
    const deltaSec = Math.round(
      (next.secondTimestamp - current.secondTimestamp) / 1000
    );

    if (deltaSec <= 1) continue;

    const missingTimestamps: number[] = [];
    for (let step = 1; step < deltaSec; step += 1) {
      missingTimestamps.push(current.secondTimestamp + step * 1000);
    }

    gaps.push({
      fromTimestamp: current.secondTimestamp,
      toTimestamp: next.secondTimestamp,
      missingTimestamps,
    });
  }

  return { duplicates, gaps };
};

export const hasRepairableIssues = (issues: HrDataIssues) =>
  issues.duplicates.length > 0 || issues.gaps.length > 0;

export const countExpectedGapInsertions = (issues: HrDataIssues) =>
  issues.gaps.reduce((sum, gap) => sum + gap.missingTimestamps.length, 0);
